<template>
  <div>
    <div @click="tooltip = !tooltip">
      <GenericInput
        id="tooltip-trigger"
        v-model="getDate"
        autocomplete="off"
        input-classes="pr-[40px]"
        :class="triggerClasses"
        :legend-text="false"
        :text="$t('enter_the_date')"
        icon="calendar"
      />
    </div>

    <div
      v-show="tooltip"
      id="tooltip"
      data-popper-content
      class="w-[208px] shadow-event-content z-[50] bg-white absolute"
      :class="tooltipClasses"
    >
      <div data-popper-arrow class="absolute right-[10px]"></div>
      <div class="px-[17px] py-[10px] bg-white z-[11] relative">
        <!-- TITLE November 2023 -->
        <div class="w-full flex justify-between mb-[7px]">
          <div
            class="text-[11px] font-normal leading-[18px] tracking-[-0.11px]"
          >
            {{ moment(calendarDate).format('MMMM') }}
            {{ moment(calendarDate).format('YYYY') }}
          </div>
          <div class="flex items-center justify-center gap-x-[5px]">
            <span
              class="cursor-pointer"
              @click="
                calendarDate = moment(calendarDate)
                  .subtract(1, 'month')
                  .format('YYYY-MM-DD')
              "
            >
              <InlineSvg src="/assets/svg/icons/prev.svg" />
            </span>
            <span
              class="cursor-pointer"
              @click="
                calendarDate = moment(calendarDate)
                  .add(1, 'month')
                  .format('YYYY-MM-DD')
              "
            >
              <InlineSvg src="/assets/svg/icons/next.svg" />
            </span>
          </div>
        </div>
        <!-- CALENDAR -->
        <div id="week-calendar" class="grid grid-cols-7 gap-y-[7px]">
          <span
            v-for="(n, i) in dayNames"
            :key="i"
            class="text-[10px] text-center text-grey-pale font-bold leading-[14px] tracking-[-0.1px]"
          >
            {{ n }}
          </span>
          <span
            v-for="(d, i) in monthDays"
            :key="i"
            class="text-[10px] flex items-center justify-center leading-[14px] tracking-[-0.1px] p-[4px] z-[2]"
            :class="{
              'text-grey-pale cursor-not-allowed':
                d < moment().format('YYYY-MM-DD'),
              'text-charcoal cursor-pointer':
                d >= moment().format('YYYY-MM-DD'),
              'border rounded-full border-grey-pale':
                d == moment().format('YYYY-MM-DD') &&
                isDateInRange(moment(d)) === -1,
              'bg-blue text-white': isDateInRange(moment(d)) != -1,
              'rounded-l-[40px]':
                isDateInRange(moment(d)) != -1 &&
                moment(d).format('DD') ==
                  moment(d).startOf('week').format('DD'),
              'rounded-r-[40px]':
                isDateInRange(moment(d)) != -1 &&
                moment(d).format('DD') == moment(d).endOf('week').format('DD'),
            }"
            @click="handleSelectDate(d)"
          >
            {{ moment(d).format('D') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const emit = defineEmits(['update:modelValue']);
const props = defineProps({
  modelValue: {
    type: [Array],
    default: () => [],
  },
  tooltipClasses: {
    type: String,
    default: '',
  },
});

const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

import { useHelpers } from '@/composables/useHelpers';
const { generateDateRange } = useHelpers();
const monthDays = computed(() => {
  const startDay = moment(calendarDate.value).startOf('month').startOf('week');
  const endDay = moment(calendarDate.value).endOf('month').endOf('week');
  return generateDateRange(startDay, endDay);
});

watch(
  () => props.modelValue,
  (newVal) => {
    dates.value = newVal;
  }
);
const dates = ref(props.modelValue);

import moment from 'moment';

const calendarDate = ref(moment().format('YYYY-MM-DD'));
const getDate = computed(() => {
  if (!dates.value || !dates.value.length) return '';

  // First, sort the date ranges based on the start date.
  const sortedDates = [...dates.value].sort((a, b) => {
    const startDateA = moment(a[0]);
    const startDateB = moment(b[0]);
    return startDateA.diff(startDateB);
  });

  // Now format the sorted date ranges.
  // if order dont need just comment top section and change sortedDated to dates.value
  const formattedRanges = sortedDates.map((range) => {
    const start = moment(range[0]);
    const end = moment(range[1]);
    return `${start.format('MMMM DD')}-${end.format('DD')}`;
  });

  return formattedRanges.join(', ');
});

const handleDate = (val) => {
  if (props.range) return emit('update:modelValue', dates.value);

  emit('update:modelValue', moment(dates.value).format('YYYY-MM-DD'));
};

const tooltip = ref(false);

const closeTooltipOnOutsideClick = (event) => {
  if (tooltip.value) {
    const tooltipContent = document.getElementById('tooltip');
    const trigger = document.getElementById('tooltip-trigger');
    if (
      tooltipContent &&
      !tooltipContent.contains(event.target) &&
      !trigger.contains(event.target)
    ) {
      tooltip.value = false;
    }
  }
};

const isDateInRange = (date) => {
  for (let i = 0; i < dates.value.length; i++) {
    const range = dates.value[i];
    if (date.isBetween(moment(range[0]), moment(range[1]), null, '[]')) {
      return i; // Return index of the range if the date is inside it
    }
  }
  return -1; // Return -1 if the date isn't in any range
};

const handleSelectDate = (date) => {
  const rangeIndex = isDateInRange(moment(date));

  if (rangeIndex > -1) {
    // The clicked date is in the range, so we remove that range from dates.value
    dates.value.splice(rangeIndex, 1);
  } else {
    // append bg blue
    // const bgBlue = `<div class="h-[20px] bg-blue col-span-7 rounded-[40px] absolute bottom-[${10}px] left-[14px] w-[180px]"></div>`;
    // const calendarContent = document.getElementById('week-calendar');

    // calendarContent.innerHTML += bgBlue;

    // The clicked date is not in the range, so we add new range to dates.value
    const startDate = moment(date).startOf('week');
    const endDate = moment(date).endOf('week');

    if (moment(endDate).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD'))
      return;
    dates.value.push([
      startDate.format('YYYY-MM-DD'),
      endDate.format('YYYY-MM-DD'),
    ]);
  }
};

onMounted(() => {
  document.addEventListener('click', closeTooltipOnOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener('click', closeTooltipOnOutsideClick);
});
</script>
